import React, { useEffect, useState } from "react";

import snakeToPascal, { isFloat, round } from "../../../tools";
import apiService from "../../../services/ApiService";
import dayjs from "dayjs";
import { useAppStore } from "../../../shared/stores/useAppStore";
import { DATESYSTEM_TO_FORMAT } from "../../../pages/subpages/Settings";
import FieldDependenciesPanel from "./reusables/FieldDependenciesPanel";


export default function DynamicGridField({ name, field, compareWithField, scrollRef, onScroll, scenarioId = null, onNavigate = undefined }) {
    const userSettings = useAppStore(s => s.userSettings);

    // Build a comparison structure from compareWithField for quick lookup
    const compareGrid = React.useMemo(() => {
        if (!compareWithField?.value?.value) return null;
        return compareWithField.value.value;
    }, [compareWithField]);

    const isDifferent = (rowIndex: number, colIndex: number, currentValue: any) => {
        if (!compareGrid || !compareGrid[rowIndex] || compareGrid[rowIndex][colIndex] === undefined) return false;
        const compareValue = compareGrid[rowIndex][colIndex];

        // For numbers, use tolerance comparison
        if (typeof currentValue === 'number' && typeof compareValue === 'number') {
            return Math.abs(currentValue - compareValue) > 0.01;
        }

        // For other types, use strict equality
        return currentValue !== compareValue;
    };

    const [showDeps, setShowDeps] = useState(false);

    const headerRow = field.value.value[0] || [];
    const dataRows = field.value.value.slice(1);

    return (
        <div className="relative z-0">
        <div className="overflow-auto relative z-0" ref={scrollRef} onScroll={onScroll}>
            <table className="w-full border-collapse text-sm">
                <thead className="bg-slate-50 sticky top-0 z-20">
                    <tr className="border-b border-slate-200">
                        {headerRow.map((elem, colIndex) => {
                            const formatted = dayjs(elem, 'YYYY-MM-DD', true).isValid()
                                ? dayjs(elem).format(DATESYSTEM_TO_FORMAT[userSettings.dateSystem])
                                : elem;
                            return (
                                <th
                                    key={colIndex}
                                    className={`p-2 text-xs font-medium text-slate-500 ${colIndex === 0 ? 'text-left sticky left-0 z-30 bg-slate-50 min-w-[160px]' : 'text-center min-w-[100px]'}`}
                                >
                                    {formatted}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {dataRows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-b border-slate-200 hover:bg-slate-50">
                            {row.map((elem, colIndex) => {
                                const isDataCell = colIndex > 0;
                                const hasDifference = isDifferent(rowIndex + 1, colIndex, elem);

                                // First column: row label
                                if (colIndex === 0) {
                                    return (
                                        <td
                                            key={colIndex}
                                            className={`p-2 text-left font-semibold text-slate-700 sticky left-0 z-10 bg-white border-r border-slate-100 ${hasDifference ? 'bg-yellow-100' : ''}`}
                                        >
                                            {elem}
                                        </td>
                                    );
                                }

                                // Data cells: apply percentage + rounding
                                if (isDataCell && isFloat(elem)) {
                                    const cellValue = field.is_percentage ? elem * 100 : elem;
                                    const suffix = field.is_percentage ? ' %' : '';
                                    const rounded = round(cellValue, userSettings.roundPrecision);
                                    const formatted = rounded.toLocaleString(userSettings.numberFormattingLocale, { maximumFractionDigits: userSettings.roundPrecision });
                                    return (
                                        <td key={colIndex} className={`p-2 text-center ${hasDifference ? 'bg-yellow-100 font-bold' : ''}`}>
                                            {formatted}{suffix}
                                        </td>
                                    );
                                }

                                return (
                                    <td key={colIndex} className={`p-2 text-center ${hasDifference ? 'bg-yellow-100 font-bold' : ''}`}>
                                        {elem}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        </div>
    )
}
